import React, { useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend as ReLegend,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { getAllAttendances } from "../../../services/attendanceServices";

/* ===== Helpers ===== */
const monthLabelFR = (yyyyMM) => {
  const [y, m] = yyyyMM.split("-").map(Number);
  // Abréviation FR courte
  const formatter = new Intl.DateTimeFormat("fr-FR", { month: "short" });
  // Utilise le 1er du mois pour formater
  return formatter.format(new Date(y, m - 1, 1));
};

/** Génère la liste des mois de la saison, 01/09 -> 30/06 (ex: "2024-2025" => ["2024-09",...,"2025-06"]) */
const generateSeasonMonthsSepToJun = (season) => {
  if (!season || !/^\d{4}-\d{4}$/.test(season)) return [];
  const [startY, endY] = season.split("-").map(Number);
  const months = [];
  // Sep..Dec (année startY)
  for (let m = 9; m <= 12; m++)
    months.push(`${startY}-${String(m).padStart(2, "0")}`);
  // Jan..Jun (année endY)
  for (let m = 1; m <= 6; m++)
    months.push(`${endY}-${String(m).padStart(2, "0")}`);
  return months;
};

/** Filtre les présences pour un set d'adhérents et une saison Sep->Jun */
const filterSeasonForGroup = (attendances, adherentIdSet, season) => {
  const months = generateSeasonMonthsSepToJun(season);
  const prefixSet = new Set(months); // "YYYY-MM"
  return (attendances || []).filter((a) => {
    if (!adherentIdSet.has(a.adherent_id)) return false;
    const prefix = String(a.date_presence).slice(0, 7);
    return prefixSet.has(prefix);
  });
};

/** Agrège par mois: présents, absents, taux (%) */
const buildMonthlySeries = (season, attendances) => {
  const months = generateSeasonMonthsSepToJun(season);
  const byMonth = new Map(months.map((m) => [m, { present: 0, absent: 0 }]));
  for (const a of attendances) {
    const key = String(a.date_presence).slice(0, 7);
    if (!byMonth.has(key)) continue;
    if (a.presence === true) byMonth.get(key).present += 1;
    else if (a.presence === false) byMonth.get(key).absent += 1;
  }
  return months.map((m) => {
    const { present, absent } = byMonth.get(m);
    const total = present + absent;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;
    return { month: m, label: monthLabelFR(m), present, absent, total, rate };
  });
};

/* ===== Component ===== */
const ChartsPanel = ({
  isOpen,
  seasonActive, // "YYYY-YYYY" (depuis settings)
  selectedGroup, // objet groupe (optionnel)
  adherents, // liste des adhérents (du groupe sélectionné)
  allAttendances, // cache depuis le parent si dispo
  setAllAttendances, // setter du parent (pour mutualiser le cache)
}) => {
  // Charge toutes les présences si besoin
  useEffect(() => {
    const load = async () => {
      if (!isOpen) return;
      if (allAttendances && allAttendances.length) return;
      try {
        const data = await getAllAttendances();
        setAllAttendances?.(data || []);
      } catch (e) {
        console.error("[ChartsPanel] getAllAttendances error:", e);
      }
    };
    load();
  }, [isOpen, allAttendances, setAllAttendances]);

  // Datasets
  const adherentIdSet = useMemo(
    () => new Set((adherents || []).map((a) => a.id)),
    [adherents]
  );

  const seasonData = useMemo(() => {
    if (!seasonActive || !allAttendances || !adherentIdSet.size) return [];
    const filtered = filterSeasonForGroup(
      allAttendances,
      adherentIdSet,
      seasonActive
    );
    return buildMonthlySeries(seasonActive, filtered);
  }, [seasonActive, allAttendances, adherentIdSet]);

  const kpis = useMemo(() => {
    const totalPresent = seasonData.reduce((s, x) => s + x.present, 0);
    const totalAbsent = seasonData.reduce((s, x) => s + x.absent, 0);
    const totalMarked = totalPresent + totalAbsent;
    const avgRate =
      seasonData.length > 0
        ? Math.round(
            seasonData.reduce((s, x) => s + x.rate, 0) / seasonData.length
          )
        : 0;
    return { totalPresent, totalAbsent, totalMarked, avgRate };
  }, [seasonData]);

  if (!isOpen) return null;

  return (
    <div
      className="
        mt-4 rounded-2xl border border-slate-200 dark:border-slate-800
        bg-white/80 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5
      "
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base sm:text-lg font-semibold">
          Graphiques – {selectedGroup?.nom || "Groupe"} · Saison {seasonActive}
        </h3>
        <div className="flex gap-2 text-xs sm:text-sm">
          <span className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-2 py-1">
            Présences: <strong className="ml-1">{kpis.totalPresent}</strong>
          </span>
          <span className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-2 py-1">
            Absences: <strong className="ml-1">{kpis.totalAbsent}</strong>
          </span>
          <span className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-2 py-1">
            Taux moyen: <strong className="ml-1">{kpis.avgRate}%</strong>
          </span>
        </div>
      </div>

      {/* Charts */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Bar chart Présences/Absences par mois */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-3">
          <p className="text-sm font-medium mb-2">
            Présences / Absences par mois
          </p>
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={seasonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <ReLegend />
                <Bar
                  dataKey="present"
                  name="Présents"
                  stackId="a"
                  fill="#10B981" // vert (emerald-500)
                  fillOpacity={0.9}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="absent"
                  name="Absents"
                  stackId="a"
                  fill="#EF4444" // rouge (rose-500)
                  fillOpacity={0.9}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Line chart Taux (%) par mois */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/60 p-3">
          <p className="text-sm font-medium mb-2">
            Taux de présence (%) par mois
          </p>
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={seasonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <ReLegend />
                <Line type="monotone" dataKey="rate" name="Taux (%)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Note */}
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
        Saison comptabilisée du 1er septembre au 30 juin.
      </p>
    </div>
  );
};

export default ChartsPanel;
